import React, { useEffect, useMemo, useState } from "react";
import { Edit, Loader2, Plus, Save, Trash2, X, PlusCircle, ArrowLeft, BookCopy, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import axios from "../../api/axios";

// Import unified PaginationSearchbar component
import PaginationSearchbar from "../../GlobalComponents/PaginationSearchbar";

// Import central theme classes from MasterCssClass.js
import {
  masterFormRowSingle,
  masterFormCard,
  masterFormFieldGroup,
  masterInputLabel,
  masterInput,
  masterSelect,
  masterFormBtnSubmit,
  masterBtnBack,
  masterSubCard,
  masterInnerCard,
  masterActionButton,
  masterDeleteButton,
  masterPanelCard,
  masterHeadingMain,
  masterTableWrap,
  masterTable,
  masterTableHead,
  masterTableRow,
  masterBadgeLive,
  masterBadgeInactive,
  masterActionBtnEdit,
  masterActionBtnDelete,
  masterPageContainer,
  masterHeaderContent,
  masterHeaderBrand,
  masterHeaderIconBox,
  masterHeaderActions,
  masterTableContainer,
  masterThCode,
  masterThName,
  masterThStatus,
  masterThManage,
  masterTdCode,
  masterTdName,
  masterTdStatus,
  masterTdManage,
  masterTdManageFlex,
  masterTdEmpty,
} from "../../GlobalComponents/MasterCssClass";

const emptyBulkTopic = { topicName: "", subTopicInput: "", subTopics: [] };
const emptyTopicDraft = { topicName: "", subTopicInput: "", subTopics: [] };

const SyllabusMaster = () => {
  // View mode state: "form" (default) or "list"
  const [viewMode, setViewMode] = useState("form");

  const [editId, setEditId] = useState(null);
  const isEdit = Boolean(editId);

  const [exams, setExams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [syllabusRows, setSyllabusRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    examCode: "",
    examStage: "",
    categoryId: "",
    subjectName: "",
    status: "ACTIVE",
    topics: [],
  });

  const [currentTopic, setCurrentTopic] = useState({ ...emptyTopicDraft });

  const [copyEnabled, setCopyEnabled] = useState(false);
  const [copyFromStage, setCopyFromStage] = useState("");
  const [bulkSubjects, setBulkSubjects] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  // --- LIST CONTROL & FILTER STATES ---
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [listFilters, setListFilters] = useState({
    examCode: "",
    examStage: "",
    catId: "",
  });

  const fetchBase = async () => {
    setLoading(true);
    try {
      const [examRes, catRes, sylRes] = await Promise.allSettled([
        axios.get("/master/exam"),
        axios.get("/master/category/all"),
        axios.get("/master/syllabus/all"),
      ]);

      const examList = examRes.status === "fulfilled"
        ? (Array.isArray(examRes.value.data) ? examRes.value.data : examRes.value.data?.data || [])
        : [];
      const catList = catRes.status === "fulfilled"
        ? (Array.isArray(catRes.value.data) ? catRes.value.data : catRes.value.data?.data || [])
        : [];
      const syllabusList = sylRes.status === "fulfilled"
        ? (Array.isArray(sylRes.value.data) ? sylRes.value.data : sylRes.value.data?.data || [])
        : [];

      setExams(examList);
      setCategories(catList);
      setSyllabusRows(syllabusList);
    } catch (err) {
      toast.error(err?.response?.data?.message || "DATABASE SYNC FAILED");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBase();
  }, []);

  const loadEditData = async (recordId) => {
    setLoading(true);
    try {
      const res = await axios.get(`/master/syllabus/${recordId}`);
      const row = res.data?.data;
      if (!row) {
        toast.error("SYLLABUS NOT FOUND");
        setViewMode("list");
        return;
      }

      const exam = exams.find(
        (ex) => String(ex._id) === String(row.examId) || String(ex.examCode || "").toUpperCase() === String(row.examCode || "").toUpperCase()
      );
      const cat = categories.find(
        (c) =>
          String(c._id) === String(row.categoryId) ||
          (String(c.catId || "").toUpperCase() === String(row.catId || "").toUpperCase() &&
            String(c.examCode || "").toUpperCase() === String(row.examCode || "").toUpperCase())
      );

      setFormData({
        examCode: exam?.examCode || row.examCode || "",
        examStage: row.examStage || cat?.examStage || "",
        categoryId: cat?.catId || row.catId || "",
        subjectName: row.subjectName || "",
        status: row.status || "ACTIVE",
        topics: (row.topics || []).map((t) => ({
          topicName: t.topicName || "",
          subTopicInput: "",
          subTopics: (t.subTopics || []).map((sub) => {
            if (typeof sub === "string") {
              return { subTopicName: sub, nestedTopics: [], nestedTopicInput: "" };
            }
            return {
              subTopicName: sub.subTopicName || sub.name || "",
              nestedTopicInput: "",
              nestedTopics: (sub.nestedTopics || sub.childTopics || []).map((nt) => {
                if (typeof nt === "string") {
                  return { nestedTopicName: nt, nestedSubTopicInput: "", nestedSubTopics: [] };
                }
                return {
                  nestedTopicName: nt.nestedTopicName || nt.name || "",
                  nestedSubTopicInput: "",
                  nestedSubTopics: nt.nestedSubTopics || nt.childTopics || [],
                };
              }),
            };
          }),
        })),
      });
      setCurrentTopic({ ...emptyTopicDraft });
      setCopyEnabled(false);
      setCopyFromStage("");
      setBulkSubjects([]);
      setBulkLoading(false);
    } catch (err) {
      toast.error("FAILED TO LOAD RECORD");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditId(null);
    setFormData({
      examCode: "",
      examStage: "",
      categoryId: "",
      subjectName: "",
      status: "ACTIVE",
      topics: [],
    });
    setCurrentTopic({ ...emptyTopicDraft });
    resetCopyState();
  };

  const filteredCategories = useMemo(() => {
    const selectedExam = exams.find((ex) => String(ex.examCode) === String(formData.examCode));
    if (!selectedExam) return [];
    return categories.filter(
      (cat) => String(cat.examCode || "").toUpperCase() === String(selectedExam.examCode || "").toUpperCase()
    );
  }, [categories, exams, formData.examCode]);

  const selectedExamForForm = useMemo(
    () => exams.find((ex) => String(ex.examCode) === String(formData.examCode)) || null,
    [exams, formData.examCode]
  );

  const selectedCategoryForForm = useMemo(
    () => categories.find((cat) => String(cat.catId) === String(formData.categoryId)) || null,
    [categories, formData.categoryId]
  );

  const stageOptionsForForm = useMemo(() => {
    const selectedCategory = categories.find((cat) => String(cat.catId) === String(formData.categoryId));
    return Array.from(
      new Set(
        [ ...(Array.isArray(selectedCategory?.examStages) ? selectedCategory.examStages : []), selectedCategory?.examStage ]
          .map((stage) => String(stage || "").trim().toUpperCase())
          .filter(Boolean)
      )
    );
  }, [categories, formData.categoryId]);

  const existingStagesForCopy = useMemo(() => {
    if (isEdit) return [];
    const examCode = String(selectedExamForForm?.examCode || "").trim().toUpperCase();
    const catId = String(selectedCategoryForForm?.catId || "").trim().toUpperCase();
    const targetStage = String(formData.examStage || "").trim().toUpperCase();
    if (!examCode) return [];

    const stages = new Set();
    syllabusRows.forEach((row) => {
      if (String(row.examCode || "").trim().toUpperCase() !== examCode) return;
      if (String(row.catId || "").trim().toUpperCase() !== catId) return;
      const st = String(row.examStage || "").trim().toUpperCase();
      if (st) stages.add(st);
    });

    return Array.from(stages)
      .filter((st) => !targetStage || st !== targetStage)
      .sort();
  }, [formData.examStage, isEdit, selectedCategoryForForm?.catId, selectedExamForForm?.examCode, syllabusRows]);

  const canCopyFromStage = useMemo(() => {
    const targetStage = String(formData.examStage || "").trim();
    return !isEdit && !!selectedExamForForm && !!selectedCategoryForForm && !!targetStage && existingStagesForCopy.length > 0;
  }, [existingStagesForCopy.length, formData.examStage, isEdit, selectedCategoryForForm, selectedExamForForm]);

  const resetCopyState = () => {
    setCopyEnabled(false);
    setCopyFromStage("");
    setBulkSubjects([]);
    setBulkLoading(false);
  };

  const fetchStageTemplate = async (stage) => {
    const examCode = String(selectedExamForForm?.examCode || "").trim().toUpperCase();
    const catId = String(selectedCategoryForForm?.catId || "").trim().toUpperCase();
    const fromStage = String(stage || "").trim().toUpperCase();
    if (!examCode || !fromStage) return;

    setBulkLoading(true);
    try {
      const res = await axios.get("/master/syllabus/all", {
        params: { examCode, catId, examStage: fromStage },
      });
      const rows = Array.isArray(res.data) ? res.data : res.data?.data || [];
      const next = (rows || []).map((r) => ({
        key: String(r._id || `${r.subjectName}-${Math.random()}`),
        subjectName: String(r.subjectName || "").toUpperCase(),
        topics: (r.topics || []).map((t) => ({
          topicName: String(t.topicName || "").toUpperCase(),
          subTopics: (t.subTopics || []).map((s) => (typeof s === "string" ? { subTopicName: s.toUpperCase(), nestedTopics: [] } : s)),
          subTopicInput: "",
        })),
      }));
      setBulkSubjects(next);
      toast.success(`FETCHED ${next.length} SUBJECT(S)`);
    } catch (err) {
      setBulkSubjects([]);
      toast.error(err?.response?.data?.message || "FAILED TO FETCH STAGE DATA");
    } finally {
      setBulkLoading(false);
    }
  };

  const updateBulkSubject = (index, patch) => {
    setBulkSubjects((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const addBulkSubject = () => {
    setBulkSubjects((prev) => [
      ...prev,
      { key: `new-${Date.now()}-${Math.random()}`, subjectName: "", topics: [] },
    ]);
  };

  const removeBulkSubject = (index) => {
    setBulkSubjects((prev) => prev.filter((_, i) => i !== index));
  };

  const addBulkTopic = (subjectIndex) => {
    setBulkSubjects((prev) =>
      prev.map((s, i) =>
        i === subjectIndex ? { ...s, topics: [ ...(s.topics || []), { ...emptyBulkTopic } ] } : s
      )
    );
  };

  const removeBulkTopic = (subjectIndex, topicIndex) => {
    setBulkSubjects((prev) =>
      prev.map((s, i) =>
        i === subjectIndex ? { ...s, topics: (s.topics || []).filter((_, tIdx) => tIdx !== topicIndex) } : s
      )
    );
  };

  const updateBulkTopic = (subjectIndex, topicIndex, patch) => {
    setBulkSubjects((prev) =>
      prev.map((s, i) => {
        if (i !== subjectIndex) return s;
        const topics = (s.topics || []).map((t, tIdx) => (tIdx === topicIndex ? { ...t, ...patch } : t));
        return { ...s, topics };
      })
    );
  };

  const addBulkSubTopic = (subjectIndex, topicIndex) => {
    setBulkSubjects((prev) =>
      prev.map((s, i) => {
        if (i !== subjectIndex) return s;
        const topics = (s.topics || []).map((t, tIdx) => {
          if (tIdx !== topicIndex) return t;
          const value = String(t.subTopicInput || "").trim().toUpperCase();
          if (!value) return t;
          return { ...t, subTopics: [ ...(t.subTopics || []), { subTopicName: value, nestedTopics: [], nestedTopicInput: "" } ], subTopicInput: "" };
        });
        return { ...s, topics };
      })
    );
  };

  const removeBulkSubTopic = (subjectIndex, topicIndex, subIndex) => {
    setBulkSubjects((prev) =>
      prev.map((s, i) => {
        if (i !== subjectIndex) return s;
        const topics = (s.topics || []).map((t, tIdx) => {
          if (tIdx !== topicIndex) return t;
          return { ...t, subTopics: (t.subTopics || []).filter((_, sIdx) => sIdx !== subIndex) };
        });
        return { ...s, topics };
      })
    );
  };

  // --- DRAFT / SAVED TOPIC LIST HANDLERS ---
  const addSubTopicToDraft = () => {
    const value = String(currentTopic?.subTopicInput || "").trim().toUpperCase();
    if (!value) return;
    setCurrentTopic((prev) => ({
      ...prev,
      subTopics: [
        ...(prev.subTopics || []),
        { subTopicName: value, nestedTopics: [], nestedTopicInput: "" },
      ],
      subTopicInput: "",
    }));
  };

  const removeSubTopicFromDraft = (subIndex) => {
    setCurrentTopic((prev) => ({
      ...prev,
      subTopics: prev.subTopics.filter((_, sIdx) => sIdx !== subIndex),
    }));
  };

  const addNestedTopicToDraftSub = (subIndex) => {
    setCurrentTopic((prev) => {
      const updatedSubTopics = prev.subTopics.map((sub, sIdx) => {
        if (sIdx !== subIndex) return sub;
        const val = String(sub.nestedTopicInput || "").trim().toUpperCase();
        if (!val) return sub;
        return {
          ...sub,
          nestedTopics: [
            ...(sub.nestedTopics || []),
            { nestedTopicName: val, nestedSubTopics: [], nestedSubTopicInput: "" },
          ],
          nestedTopicInput: "",
        };
      });
      return { ...prev, subTopics: updatedSubTopics };
    });
  };

  const removeNestedTopicFromDraftSub = (subIndex, nestedIndex) => {
    setCurrentTopic((prev) => {
      const updatedSubTopics = prev.subTopics.map((sub, sIdx) => {
        if (sIdx !== subIndex) return sub;
        return {
          ...sub,
          nestedTopics: (sub.nestedTopics || []).filter((_, nIdx) => nIdx !== nestedIndex),
        };
      });
      return { ...prev, subTopics: updatedSubTopics };
    });
  };

  const addNestedSubTopicToDraftNested = (subIndex, nestedIndex) => {
    setCurrentTopic((prev) => {
      const updatedSubTopics = prev.subTopics.map((sub, sIdx) => {
        if (sIdx !== subIndex) return sub;
        const updatedNestedTopics = sub.nestedTopics.map((nt, nIdx) => {
          if (nIdx !== nestedIndex) return nt;
          const subVal = String(nt.nestedSubTopicInput || "").trim().toUpperCase();
          if (!subVal) return nt;
          return {
            ...nt,
            nestedSubTopics: [...(nt.nestedSubTopics || []), subVal],
            nestedSubTopicInput: "",
          };
        });
        return { ...sub, nestedTopics: updatedNestedTopics };
      });
      return { ...prev, subTopics: updatedSubTopics };
    });
  };

  const removeNestedSubTopicFromDraftNested = (subIndex, nestedIndex, subSubIndex) => {
    setCurrentTopic((prev) => {
      const updatedSubTopics = prev.subTopics.map((sub, sIdx) => {
        if (sIdx !== subIndex) return sub;
        const updatedNestedTopics = sub.nestedTopics.map((nt, nIdx) => {
          if (nIdx !== nestedIndex) return nt;
          return {
            ...nt,
            nestedSubTopics: (nt.nestedSubTopics || []).filter((_, ssIdx) => ssIdx !== subSubIndex),
          };
        });
        return { ...sub, nestedTopics: updatedNestedTopics };
      });
      return { ...prev, subTopics: updatedSubTopics };
    });
  };

  const saveTopicDraft = () => {
    const topicName = String(currentTopic.topicName || "").trim().toUpperCase();
    if (!topicName) {
      toast.error("PLEASE ENTER TOPIC NAME");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      topics: [...prev.topics, { ...currentTopic }],
    }));
    setCurrentTopic({ ...emptyTopicDraft });
    toast.success("TOPIC SAVED TO LIST");
  };

  const removeSavedTopic = (idx) => {
    setFormData((prev) => ({
      ...prev,
      topics: prev.topics.filter((_, i) => i !== idx),
    }));
  };

  const editSavedTopic = (idx) => {
    const topic = formData.topics[idx];
    if (!topic) return;
    setCurrentTopic({
      topicName: topic.topicName || "",
      subTopicInput: "",
      subTopics: JSON.parse(JSON.stringify(topic.subTopics || [])),
    });
    setFormData((prev) => ({
      ...prev,
      topics: prev.topics.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.examCode) return toast.error("PLEASE SELECT EXAM");

    if (copyEnabled) {
      if (!String(formData.examStage || "").trim()) return toast.error("PLEASE SELECT STAGE");
      if (!Array.isArray(bulkSubjects) || bulkSubjects.length === 0) return toast.error("NO SUBJECTS TO SAVE");
    } else {
      if (!formData.subjectName.trim()) return toast.error("PLEASE ENTER SUBJECT NAME");
      if (formData.topics.length === 0) return toast.error("PLEASE SAVE AT LEAST ONE TOPIC");
    }

    setIsSaving(true);
    try {
      if (copyEnabled) {
        const payload = {
          examCode: formData.examCode,
          categoryId: formData.categoryId || undefined,
          examStage: String(formData.examStage || "").trim().toUpperCase(),
          status: formData.status,
          subjects: bulkSubjects.map((s) => ({
            subjectName: String(s.subjectName || "").trim().toUpperCase(),
            topics: (s.topics || [])
              .map((t) => ({
                topicName: String(t.topicName || "").trim().toUpperCase(),
                subTopics: (t.subTopics || []).map((sub) => (typeof sub === "string" ? sub : sub)),
              }))
              .filter((t) => t.topicName),
          })),
        };

        const res = await axios.post("/master/syllabus/bulk-create", payload);
        if (res.data.success) {
          toast.success(`SAVED ${res.data?.count || payload.subjects.length}`);
          resetForm();
          fetchBase();
        }
      } else {
        const payload = {
          id: isEdit ? editId : undefined,
          examCode: formData.examCode,
          examStage: formData.examStage || undefined,
          categoryId: formData.categoryId || undefined,
          subjectName: formData.subjectName.toUpperCase(),
          topics: formData.topics.map((t) => ({
            topicName: String(t.topicName || "").trim().toUpperCase(),
            subTopics: (t.subTopics || []).map((sub) => {
              if (typeof sub === "string") return sub;
              return {
                subTopicName: String(sub.subTopicName || sub.name || "").trim().toUpperCase(),
                nestedTopics: (sub.nestedTopics || sub.childTopics || []).map((nt) => ({
                  nestedTopicName: String(nt.nestedTopicName || nt.name || "").trim().toUpperCase(),
                  nestedSubTopics: (nt.nestedSubTopics || nt.childTopics || []).map((s) => String(s || "").trim().toUpperCase()).filter(Boolean),
                })),
              };
            }),
          })).filter((t) => t.topicName),
          status: formData.status,
        };
        const res = await axios.post("/master/syllabus/upsert", payload);
        if (res.data.success) {
          toast.success(isEdit ? "UPDATED" : "SAVED");
          resetForm();
          fetchBase();
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "SAVE FAILED");
    } finally {
      setIsSaving(false);
    }
  };

  // --- LIST ACTIONS & FILTERS ---
  const filteredRows = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return syllabusRows.filter((row) => {
      const matchesFilters =
        (!listFilters.examCode ||
          String(row.examCode || "").toUpperCase() === String(listFilters.examCode || "").toUpperCase()) &&
        (!listFilters.examStage ||
          String(row.examStage || "").toUpperCase() === String(listFilters.examStage || "").toUpperCase()) &&
        (!listFilters.catId ||
          String(row.catId || "").toUpperCase() === String(listFilters.catId || "").toUpperCase());

      const matchesSearch =
        (row.syllabusId || "").toLowerCase().includes(q) ||
        (row.examName || "").toLowerCase().includes(q) ||
        (row.catName || "").toLowerCase().includes(q) ||
        (row.subjectName || "").toLowerCase().includes(q) ||
        (row.status || "").toLowerCase().includes(q);

      return matchesFilters && matchesSearch;
    });
  }, [listFilters.catId, listFilters.examCode, listFilters.examStage, searchTerm, syllabusRows]);

  const listFilterCategories = useMemo(() => {
    if (!listFilters.examCode) return [];
    return categories.filter(
      (cat) => String(cat.examCode || "").toUpperCase() === String(listFilters.examCode || "").toUpperCase()
    );
  }, [categories, listFilters.examCode]);

  const stageOptionsForList = useMemo(() => {
    if (!listFilters.examCode || !listFilters.catId) return [];
    const selectedCategory = categories.find(
      (cat) =>
        String(cat.examCode || "").toUpperCase() === String(listFilters.examCode || "").toUpperCase() &&
        String(cat.catId || "").toUpperCase() === String(listFilters.catId || "").toUpperCase()
    );
    return Array.from(
      new Set(
        [ ...(Array.isArray(selectedCategory?.examStages) ? selectedCategory.examStages : []), selectedCategory?.examStage ]
          .map((stage) => String(stage || "").trim().toUpperCase())
          .filter(Boolean)
      )
    );
  }, [categories, listFilters.examCode, listFilters.catId]);

  const hasExamStageInList = useMemo(
    () => filteredRows.some((row) => String(row.examStage || "").trim()),
    [filteredRows]
  );

  const handleEdit = (row) => {
    setEditId(row._id);
    loadEditData(row._id);
    setViewMode("form");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("ARE YOU SURE YOU WANT TO DELETE THIS RECORD?")) return;
    try {
      await axios.delete(`/master/syllabus/${id}`);
      toast.success("DELETED SUCCESSFULLY");
      fetchBase();
    } catch {
      toast.error("DELETE FAILED");
    }
  };

  const isAll = rowsPerPage === "ALL" || rowsPerPage === -1;
  const startIndex = isAll ? 0 : (currentPage - 1) * rowsPerPage;
  const paginatedData = isAll
    ? filteredRows
    : filteredRows.slice(startIndex, startIndex + rowsPerPage);

  return (
    <div className={`${masterPageContainer} h-full flex flex-col overflow-y-auto custom-scrollbar`}>
     {viewMode === "form" ? (
        <div>
         
          <div className={`${masterPanelCard} p-1 mb-1 sticky top-0 z-20 shadow-md`}>
            <div className="flex items-center justify-between gap-1 w-full">
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <BookCopy size={13} />
                </div>
                <h1 className={`${masterHeadingMain} whitespace-nowrap`}>{isEdit ? "Edit Syllabus" : "Syllabus Form"}</h1>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <button
                  disabled={isSaving || loading}
                  type="submit"
                  form="syllabus-form"
                  className={`${masterFormBtnSubmit} whitespace-nowrap`}
                >
                  {isSaving ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Save size={12} />
                  )}
                  <span>{isEdit ? "UPDATE" : "SAVE"}</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setViewMode("list");
                  }}
                  className={`${masterFormBtnSubmit} whitespace-nowrap`}
                >
                  <Plus size={12} strokeWidth={3} />
                  <span>VIEW LIST</span>
                </button>
              </div>
            </div>
          </div>

          <form
            id="syllabus-form"
            onSubmit={handleSubmit}
            className={`${masterFormCard} flex flex-col gap-3 mt-1`}
          >
            {/* ROW 1: STATUS, EXAM, CATEGORY NAME, STAGE */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 w-full">
              {/* Exam */}
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Exam</label>
                <select
                  value={formData.examCode}
                  onChange={(e) => {
                    resetCopyState();
                    setFormData({ ...formData, examCode: e.target.value, categoryId: "", examStage: "" });
                  }}
                  className={masterSelect}
                >
                  <option value="">-- SELECT EXAM --</option>
                  {exams.map((ex) => (
                    <option key={ex.examCode} value={ex.examCode}>{ex.examName}</option>
                  ))}
                </select>
              </div>

              {/* Category Name */}
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Category Name</label>
                <select
                  value={formData.categoryId}
                  onChange={(e) => {
                    resetCopyState();
                    setFormData({ ...formData, categoryId: e.target.value, examStage: "" });
                  }}
                  className={masterSelect}
                >
                  <option value="">-- SELECT CATEGORY --</option>
                  
                  {filteredCategories.map((cat) => (
                    <option key={cat.catId} value={cat.catId}>{cat.catName}</option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value }))}
                  className={masterSelect}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>

              {/* Exam Stage */}
              {stageOptionsForForm.length > 0 && (
                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Exam Stage</label>
                  <select
                    value={formData.examStage}
                    onChange={(e) => {
                      resetCopyState();
                      setFormData({ ...formData, examStage: e.target.value });
                    }}
                    className={masterSelect}
                  >
                    <option value="">-- SELECT STAGE --</option>
                    {stageOptionsForForm.map((stage) => (
                      <option key={stage} value={stage}>{stage}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* COPY FROM EXISTING STAGE BUILDER */}
            {canCopyFromStage && (
              <div className={masterSubCard}>
                <div className={masterFormRowSingle}>
                  <label className="flex items-center gap-1.5 text-xs font-bold text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={copyEnabled}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setCopyEnabled(next);
                        if (!next) {
                          setCopyFromStage("");
                          setBulkSubjects([]);
                          return;
                        }
                        const defaultStage = existingStagesForCopy[0] || "";
                        setCopyFromStage(defaultStage);
                        if (defaultStage) fetchStageTemplate(defaultStage);
                      }}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    Copy From Existing Stage
                  </label>

                  {copyEnabled && (
                    <div className="flex items-center gap-2">
                      <select
                        value={copyFromStage}
                        onChange={(e) => {
                          const stage = e.target.value;
                          setCopyFromStage(stage);
                          setBulkSubjects([]);
                          if (stage) fetchStageTemplate(stage);
                        }}
                        className={masterSelect}
                      >
                        <option value="">-- SELECT SOURCE STAGE --</option>
                        {existingStagesForCopy.map((st) => (
                          <option key={st} value={st}>{st}</option>
                        ))}
                      </select>

                      <button
                        type="button"
                        disabled={!copyFromStage || bulkLoading}
                        onClick={() => fetchStageTemplate(copyFromStage)}
                        className={masterFormBtnSubmit}
                      >
                        {bulkLoading ? "Fetching..." : "Refetch"}
                      </button>
                    </div>
                  )}
                </div>

                {copyEnabled && (
                  <div className="flex flex-col gap-2 pt-1">
                    <div className="flex items-center justify-between">
                      <label className={masterInputLabel}>
                        Subjects to create in stage: <span className="font-bold text-slate-900">{String(formData.examStage || "").toUpperCase()}</span>
                      </label>
                      <button type="button" onClick={addBulkSubject} className={masterActionButton}>
                        <Plus size={14} />
                      </button>
                    </div>

                    {bulkSubjects.length === 0 ? (
                      <span className="text-[11px] text-slate-400 italic p-1">No subjects loaded.</span>
                    ) : (
                      bulkSubjects.map((sub, sIdx) => (
                        <div key={sub.key || sIdx} className={masterInnerCard}>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={sub.subjectName || ""}
                              onChange={(e) => updateBulkSubject(sIdx, { subjectName: e.target.value.toUpperCase() })}
                              placeholder="SUBJECT NAME"
                              className={`${masterInput} flex-1`}
                            />
                            <button type="button" onClick={() => removeBulkSubject(sIdx)} className={masterDeleteButton}>
                              <Trash2 size={14} />
                            </button>
                          </div>

                          <div className="flex items-center justify-between">
                            <span className={masterInputLabel}>Topics</span>
                            <button type="button" onClick={() => addBulkTopic(sIdx)} className={masterActionButton}>
                              <Plus size={12} />
                            </button>
                          </div>

                          {(sub.topics || []).length === 0 ? (
                            <span className="text-[11px] text-slate-400 italic">No topics yet.</span>
                          ) : (
                            (sub.topics || []).map((t, tIdx) => (
                              <div key={`${sub.key || sIdx}-t-${tIdx}`} className={masterSubCard}>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={t.topicName || ""}
                                    onChange={(e) => updateBulkTopic(sIdx, tIdx, { topicName: e.target.value.toUpperCase() })}
                                    placeholder="TOPIC NAME"
                                    className={`${masterInput} flex-1`}
                                  />
                                  <button type="button" onClick={() => removeBulkTopic(sIdx, tIdx)} className="text-red-500 hover:text-red-700">
                                    <X size={14} />
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* NORMAL INPUT SECTION WITH HIERARCHICAL DRAFT BUILDER & SAVED TOPICS LIST */}
            {!copyEnabled && (
              <>
                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Subject Name</label>
                  <input
                    type="text"
                    value={formData.subjectName}
                    onChange={(e) => setFormData({ ...formData, subjectName: e.target.value.toUpperCase() })}
                    placeholder="ENTER SUBJECT NAME"
                    className={masterInput}
                  />
                </div>

                <div className={masterSubCard}>
               <div className="flex items-center justify-between mb-2 gap-2">
                  <label className={masterInputLabel}>Hierarchical Topic & Sub-Topic Builder</label>
                  <button 
                    type="button" 
                    onClick={saveTopicDraft} 
                    style={{ width: 'auto', flexShrink: 0 }}
                    className={`${masterFormBtnSubmit} !h-[24px] !px-2 !py-0 text-[11px]`}
                  >
                    <PlusCircle size={12} />
                    <span>Save Topic to List</span>
                  </button>
                </div>

                  {/* DRAFT CARD FOR BUILDING ONE TOPIC & ITS NESTED STRUCTURE */}
                  <div className={masterInnerCard}>
                    {/* Topic Name */}
                    <div className="mb-2">
                      <input
                        type="text"
                        placeholder="TOPIC NAME"
                        value={currentTopic.topicName || ""}
                        onChange={(e) => setCurrentTopic((prev) => ({ ...prev, topicName: e.target.value.toUpperCase() }))}
                        className={`${masterInput} font-bold`}
                      />
                    </div>

                    {/* Sub-Topic Input Row */}
                    <div className="flex items-center gap-1 mb-2">
                      <input
                        type="text"
                        placeholder="SUB-TOPIC NAME"
                        value={currentTopic.subTopicInput || ""}
                        onChange={(e) => setCurrentTopic((prev) => ({ ...prev, subTopicInput: e.target.value.toUpperCase() }))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addSubTopicToDraft();
                          }
                        }}
                        className={`${masterInput} flex-1`}
                      />
                      <button type="button" onClick={addSubTopicToDraft} className={masterActionButton} title="Add Sub-Topic">
                        <Plus size={14} />
                      </button>
                    </div>

                    {/* Sub-Topics Draft List with Nested Topics & Nested Sub-Topics */}
                    {(currentTopic.subTopics || []).length > 0 && (
                      <div className="space-y-3 mt-1 pl-4 border-l-2 border-slate-200">
                        {(currentTopic.subTopics || []).map((sub, sIdx) => (
                          <div key={`draft-sub-${sIdx}`} className="bg-slate-50 p-1.5 rounded border border-slate-200 space-y-1">
                            <div className="flex items-center justify-between uppercase text-xs font-bold text-slate-800">
                              <span>{sub.subTopicName}</span>
                              <button type="button" onClick={() => removeSubTopicFromDraft(sIdx)} className="text-red-500 hover:text-red-700">
                                <X size={12} />
                              </button>
                            </div>

                            {/* Nested Topic Input Row under Sub-topic */}
                            <div className="flex items-center gap-1">
                              <input
                                type="text"
                                placeholder="NESTED TOPIC NAME"
                                value={sub.nestedTopicInput || ""}
                                onChange={(e) => {
                                  const val = e.target.value.toUpperCase();
                                  setCurrentTopic((prev) => ({
                                    ...prev,
                                    subTopics: prev.subTopics.map((sb, j) => (j === sIdx ? { ...sb, nestedTopicInput: val } : sb)),
                                  }));
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    addNestedTopicToDraftSub(sIdx);
                                  }
                                }}
                                className={`${masterInput} flex-1 text-xs h-6`}
                              />
                              <button type="button" onClick={() => addNestedTopicToDraftSub(sIdx)} className={masterActionButton} title="Add Nested Topic">
                                <Plus size={12} />
                              </button>
                            </div>

                            {/* Nested Topics List */}
                            {(sub.nestedTopics || []).length > 0 && (
                              <div className="space-y-1 pl-3 border-l-2 border-slate-300">
                                {(sub.nestedTopics || []).map((nt, nIdx) => (
                                  <div key={`draft-nt-${nIdx}`} className="bg-white p-2 rounded border border-slate-200 space-y-1">
                                    <div className="flex items-center justify-between uppercase text-[11px] font-bold text-slate-700">
                                      <span>{nt.nestedTopicName}</span>
                                      <button type="button" onClick={() => removeNestedTopicFromDraftSub(sIdx, nIdx)} className="text-red-500 hover:text-red-700">
                                        <X size={11} />
                                      </button>
                                    </div>

                                    {/* Nested Sub-Topic Input Row */}
                                    <div className="flex items-center gap-1.5">
                                      <input
                                        type="text"
                                        placeholder="NESTED SUB-TOPIC"
                                        value={nt.nestedSubTopicInput || ""}
                                        onChange={(e) => {
                                          const val = e.target.value.toUpperCase();
                                          setCurrentTopic((prev) => ({
                                            ...prev,
                                            subTopics: prev.subTopics.map((sb, j) => {
                                              if (j !== sIdx) return sb;
                                              const nTopics = sb.nestedTopics.map((item, k) => (k === nIdx ? { ...item, nestedSubTopicInput: val } : item));
                                              return { ...sb, nestedTopics: nTopics };
                                            }),
                                          }));
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            e.preventDefault();
                                            addNestedSubTopicToDraftNested(sIdx, nIdx);
                                          }
                                        }}
                                        className={`${masterInput} flex-1 text-[11px] h-6`}
                                      />
                                      <button type="button" onClick={() => addNestedSubTopicToDraftNested(sIdx, nIdx)} className={masterActionButton} title="Add Nested Sub-Topic">
                                        <Plus size={11} />
                                      </button>
                                    </div>

                                    {/* Nested Sub-Topics List */}
                                    {(nt.nestedSubTopics || []).length > 0 && (
                                      <ol className="list-[lower-roman] list-inside text-[11px] font-medium text-slate-600 space-y-0.5 pl-1">
                                        {(nt.nestedSubTopics || []).map((subSub, ssIdx) => (
                                          <li key={`draft-sst-${ssIdx}`} className="flex items-center justify-between uppercase">
                                            <span>{subSub}</span>
                                            <button type="button" onClick={() => removeNestedSubTopicFromDraftNested(sIdx, nIdx, ssIdx)} className="text-red-500 hover:text-red-700">
                                              <X size={10} />
                                            </button>
                                          </li>
                                        ))}
                                      </ol>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* SAVED TOPICS DRAFT LIST PREVIEW */}
                  <div className="space-y-2 mt-4">
                    <p className="text-xs font-bold text-slate-700">Saved Topics List ({formData.topics.length})</p>
                    {formData.topics.length === 0 ? (
                      <span className="text-[11px] text-slate-400 italic">No topics saved to list yet.</span>
                    ) : (
                      formData.topics.map((topic, idx) => (
                        <div key={`saved-topic-${idx}`} className={masterInnerCard}>
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-bold text-slate-900 uppercase">{topic.topicName}</p>
                            <div className="flex items-center gap-1">
                              <button type="button" onClick={() => editSavedTopic(idx)} className="p-1 rounded text-blue-600 hover:bg-blue-50 transition" title="Edit Topic">
                                <Edit size={14} />
                              </button>
                              <button type="button" onClick={() => removeSavedTopic(idx)} className="p-1 rounded text-red-500 hover:bg-rose-50 transition" title="Delete Topic">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>

                          {/* Preview of Subtopics inside Saved Topic */}
                          {(topic.subTopics || []).length > 0 && (
                            <ul className="list-disc list-inside mt-1 text-xs text-slate-700 space-y-1 pl-2">
                              {(topic.subTopics || []).map((sub, sIdx) => (
                                <li key={`saved-sub-${idx}-${sIdx}`} className="uppercase">
                                  <span className="font-semibold">{sub.subTopicName}</span>
                                  {(sub.nestedTopics || []).length > 0 && (
                                    <ul className="list-circle list-inside pl-4 text-slate-600 text-[11px]">
                                      {(sub.nestedTopics || []).map((nt, nIdx) => (
                                        <li key={`saved-nt-${nIdx}`}>
                                          <span className="font-medium text-[#0E3652]">{nt.nestedTopicName}</span>
                                          {(nt.nestedSubTopics || []).length > 0 && (
                                            <ol className="list-[lower-roman] list-inside pl-4 text-slate-500">
                                              {(nt.nestedSubTopics || []).map((sst, ssIdx) => (
                                                <li key={`saved-sst-${ssIdx}`}>{sst}</li>
                                              ))}
                                            </ol>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}

            {/* ROW 4: BUTTONS (LEFT BACK) */}
            <div className="flex items-center justify-between w-full pt-1">
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setViewMode("list");
                }}
                className={`${masterBtnBack} flex items-center justify-center gap-1.5 h-[26px]`}
              >
                <ArrowLeft size={14} />
                <span>BACK</span>
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex flex-col flex-1 h-full">
          {/* HEADER CARD */}
          <div className={masterPanelCard}>
            <div className={masterHeaderContent}>
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <BookCopy size={13} />
                </div>
                <h1 className={masterHeadingMain}>Syllabus List</h1>
              </div>

              <div className={masterHeaderActions}>
                <button
                  type="button"
                  onClick={fetchBase}
                  className={masterBtnBack}
                  title="Refresh Records"
                >
                  <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                  <span>SYNC</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setViewMode("form");
                  }}
                  className={masterFormBtnSubmit}
                >
                  <Plus size={12} strokeWidth={3} />
                  <span>ADD NEW</span>
                </button>
              </div>
            </div>
          </div>

          {/* FILTER DROPDOWNS BAR */}
          <div className={masterPanelCard}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <select
                value={listFilters.examCode}
                onChange={(e) =>
                  setListFilters({ examCode: e.target.value, examStage: "", catId: "" })
                }
                className={masterSelect}
              >
                <option value="">-- ALL EXAMS --</option>
                {exams.map((ex) => (
                  <option key={ex.examCode} value={ex.examCode}>
                    {ex.examName}
                  </option>
                ))}
              </select>

              <select
                value={listFilters.catId}
                onChange={(e) => {
                  setListFilters((prev) => ({
                    ...prev,
                    catId: e.target.value,
                    examStage: "",
                  }));
                }}
                className={masterSelect}
              >
                <option value="">-- ALL CATEGORIES --</option>
                {listFilterCategories.map((cat) => (
                  <option key={cat.catId} value={cat.catId}>
                    {cat.catName}
                  </option>
                ))}
              </select>

              {stageOptionsForList.length > 0 && (
                <select
                  value={listFilters.examStage}
                  onChange={(e) => setListFilters((prev) => ({ ...prev, examStage: e.target.value }))}
                  className={masterSelect}
                >
                  <option value="">-- ALL STAGES --</option>
                  {stageOptionsForList.map((stage) => (
                    <option key={stage} value={stage}>
                      {stage}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* COMBINED SEARCH & PAGINATION BAR */}
          <PaginationSearchbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="SEARCH SYLLABUS ID, EXAM, CATEGORY, SUBJECT..."
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={setRowsPerPage}
            totalItems={filteredRows.length}
          />

          {/* TABLE CONTAINER WITH SCROLLING ENABLED */}
          <div className={`${masterTableContainer} flex-1 min-h-0 flex flex-col`}>
            <div className={`${masterTableWrap} overflow-x-auto overflow-y-auto custom-scrollbar flex-1 max-h-[calc(100vh-260px)]`}>
              <table className={masterTable}>
                <thead>
                  <tr className={masterTableHead}>
                    <th className={masterThCode}>SL NO</th>
                    <th className={masterThName}>EXAM</th>
                    <th className={masterThName}>CATEGORY</th>
                    {hasExamStageInList && <th className={masterThName}>STAGE</th>}
                    <th className={masterThName}>SUBJECT</th>
                    <th className={masterThName}>TOPICS</th>
                    <th className={masterThStatus}>STATUS</th>
                    <th className={masterThManage}>MANAGE</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-300">
                  {loading ? (
                    <tr>
                      <td colSpan={hasExamStageInList ? 8 : 7} className={masterTdEmpty}>
                        Syncing Database...
                      </td>
                    </tr>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((row,index) => (
                      <tr key={row._id} className={masterTableRow}>
                        <td className={masterTdCode}>{startIndex + index + 1}</td>
                        <td className={masterTdName}>{row.examName}</td>
                        <td className={masterTdName}>{row.catName || "---"}</td>
                        {hasExamStageInList && (
                          <td className={masterTdName}>{row.examStage || "---"}</td>
                        )}
                        <td className={masterTdName}>{row.subjectName}</td>
                        <td className="px-2 py-1 font-semibold text-slate-800">
                          <div className="space-y-1">
                            {(row.topics || []).map((topic, idx) => (
                              <div key={`${row._id}-topic-${idx}`} className="text-[11px] mb-1">
                                <p className="font-bold text-[#0E3652] uppercase">• {topic.topicName}</p>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className={masterTdStatus}>
                          <span
                            className={
                              row.status === "INACTIVE"
                                ? masterBadgeInactive
                                : masterBadgeLive
                            }
                          >
                            {row.status || "ACTIVE"}
                          </span>
                        </td>
                        <td className={masterTdManage}>
                          <div className={masterTdManageFlex}>
                            <button
                              type="button"
                              onClick={() => handleEdit(row)}
                              className={masterActionBtnEdit}
                              title="Edit"
                            >
                              <Edit size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(row._id)}
                              className={masterActionBtnDelete}
                              title="Delete"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={hasExamStageInList ? 8 : 7} className={masterTdEmpty}>
                        No Records Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyllabusMaster;